/**
 * Sales Authentication Context
 * ============================
 * Manages authentication state for the sales portal
 *
 * Features:
 * - Login/logout functionality
 * - Token management (localStorage)
 * - Current user state
 * - Loading states
 * - Auto-login from stored token
 *
 * Security Notes:
 * ---------------
 * - Uses localStorage for JWT tokens (XSS vulnerability consideration)
 * - Mitigation: 8-hour token expiration, internal sales portal only
 * - Recommended: Implement Content Security Policy (CSP) headers in nginx
 * - Backend always validates tokens on every request
 * - User data in localStorage contains no sensitive financial information
 *
 * For production deployment, ensure CSP headers are configured:
 *   Content-Security-Policy: default-src 'self'; script-src 'self'; object-src 'none';
 *
 * Alternative: Consider migrating to HTTP-only cookies in future for enhanced security
 *
 * Usage:
 *   const { user, login, logout, isAuthenticated, isLoading } = useSalesAuth();
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { salesApi, SalesUser, LoginRequest } from '../services/api/salesApi';

// =========================================
// TYPE DEFINITIONS
// =========================================

interface SalesAuthContextType {
  user: SalesUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
}

// =========================================
// CONTEXT SETUP
// =========================================

const SalesAuthContext = createContext<SalesAuthContextType | undefined>(undefined);

// =========================================
// PROVIDER COMPONENT
// =========================================

interface SalesAuthProviderProps {
  children: ReactNode;
}

export const SalesAuthProvider: React.FC<SalesAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<SalesUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-login from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if token exists
        if (salesApi.isAuthenticated()) {
          // Get user from localStorage
          const currentUser = salesApi.getCurrentUser();
          if (currentUser) {
            setUser(currentUser);
          } else {
            // Token exists but user data missing - clear everything
            salesApi.logout();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid state
        salesApi.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login with username/email and password
   */
  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      setIsLoading(true);

      // Call login API
      const response = await salesApi.login(credentials);

      if (!response.success) {
        throw new Error(response.message || 'Login failed');
      }

      // Store token and user in localStorage
      localStorage.setItem('sales_token', response.token);
      localStorage.setItem('sales_user', JSON.stringify(response.user));

      // Update state
      setUser(response.user);
    } catch (error: any) {
      console.error('Login error:', error);
      // Clear any partial state
      salesApi.logout();
      setUser(null);

      // Re-throw with user-friendly message
      throw new Error(
        error.response?.data?.detail ||
        error.message ||
        'Login failed. Please check your credentials.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout and clear all auth state
   */
  const logout = (): void => {
    // Clear localStorage
    salesApi.logout();

    // Clear state
    setUser(null);

    // Redirect to login page
    window.location.href = '/sales-horse';
  };

  // =========================================
  // CONTEXT VALUE
  // =========================================

  const value: SalesAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return (
    <SalesAuthContext.Provider value={value}>
      {children}
    </SalesAuthContext.Provider>
  );
};

// =========================================
// CUSTOM HOOK
// =========================================

/**
 * Hook to access sales auth context
 *
 * @throws Error if used outside SalesAuthProvider
 */
export const useSalesAuth = (): SalesAuthContextType => {
  const context = useContext(SalesAuthContext);

  if (context === undefined) {
    throw new Error('useSalesAuth must be used within a SalesAuthProvider');
  }

  return context;
};
